import React, { useEffect } from "react";
import { Plus, RefreshCw, Zap, Settings } from "lucide-react";
import { useHeader } from "@/shared/contexts/use-header";
import type { QuickAction } from "@/shared/contexts/HeaderContext.types";
import { useLocation } from "react-router-dom";
import { cn } from "@/shared/utils";
import { tuyaService } from "@/domains/smarthome/services";

interface QuickActionsProps {
  className?: string;
}

export const QuickActions: React.FC<QuickActionsProps> = ({ className }) => {
  const { quickActions, addQuickAction, removeQuickAction } = useHeader();
  const location = useLocation();

  // Context-aware quick actions based on current route
  useEffect(() => {
    const routeActions = getRouteQuickActions(location.pathname);

    // Wenn aktuelle QuickActions bereits den Route-Actions entsprechen, nichts tun
    const sameLength = quickActions.length === routeActions.length;
    const sameIds =
      sameLength &&
      quickActions.every(
        (action: QuickAction, index: number) => action.id === routeActions[index]?.id,
      );

    if (sameLength && sameIds) {
      return;
    }

    // Clear existing quick actions first
    const currentActionIds = quickActions.map((action: QuickAction) => action.id);
    currentActionIds.forEach((id: string) => removeQuickAction(id));

    // Add route-specific quick actions
    routeActions.forEach((action) => addQuickAction(action));
  }, [location.pathname, quickActions, addQuickAction, removeQuickAction]);

  if (quickActions.length === 0) {
    return null;
  }

  return (
    <div className={cn("flex items-center gap-2", className)}>
      {quickActions.map((action: QuickAction) => (
        <QuickActionButton key={action.id} action={action} />
      ))}
    </div>
  );
};

interface QuickActionButtonProps {
  action: QuickAction;
}

const QuickActionButton: React.FC<QuickActionButtonProps> = ({ action }) => {
  if (!action.visible) {
    return null;
  }

  return (
    <button
      onClick={action.action}
      className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-neutral-300 hover:text-white hover:bg-neutral-800/80 rounded-lg transition-all duration-200"
      title={action.shortcut ? `${action.label} (${action.shortcut})` : action.label}
    >
      {action.icon}
      <span>{action.label}</span>
    </button>
  );
};

// Route-specific quick actions
const getRouteQuickActions = (pathname: string): QuickAction[] => {
  const actions: QuickAction[] = [];

  // Use more specific path matching to avoid overlaps
  const pathSegments = pathname.split("/").filter(Boolean);

  // TimeTracQ Actions - only for exact timetracq routes
  if (pathSegments.includes("timetracq") && !pathSegments.includes("scripts")) {
    actions.push({
      id: "timetracq-new-task",
      label: "Neue Aufgabe",
      icon: <Plus className="w-4 h-4" />,
      action: () => {},
      shortcut: "⌘N",
    });
  }

  // Oura Health Actions - only for specific oura routes
  if (
    pathSegments.includes("oura") &&
    (pathSegments.includes("nexus") || pathSegments.includes("health"))
  ) {
    actions.push({
      id: "oura-sync",
      label: "Sync Oura",
      icon: <RefreshCw className="w-4 h-4" />,
      action: () => {},
      shortcut: "⌘S",
    });
  }

  // Scripts Actions - only for exact scripts routes
  if (pathSegments.includes("scripts") && !pathSegments.includes("timetracq")) {
    actions.push({
      id: "scripts-new-script",
      label: "New Script",
      icon: <Plus className="w-4 h-4" />,
      action: () => {},
      shortcut: "⌘N",
    });
  }

  // SmartHome Actions - only for exact smarthome routes
  if (pathSegments.includes("smarthome")) {
    actions.push({
      id: "smarthome-lights-on",
      label: "Lights On",
      icon: <Zap className="w-4 h-4" />,
      action: async () => {
        try {
          await tuyaService.turnOnAllLights();
        } catch {
          // NOTE: Silently ignore errors to prevent UI disruption
        }
      },
      shortcut: "⌘L",
    });
    actions.push({
      id: "smarthome-lights-off",
      label: "Lights Off",
      icon: <Zap className="w-4 h-4" />,
      action: async () => {
        try {
          await tuyaService.turnOffAllLights();
        } catch {
          // NOTE: Silently ignore errors to prevent UI disruption
        }
      },
      shortcut: "⌘⇧L",
    });
  }

  // Git Cleanup Actions - only for exact git-cleanup routes
  if (pathSegments.includes("git-cleanup")) {
    actions.push({
      id: "git-cleanup-execute",
      label: "Execute Cleanup",
      icon: <Zap className="w-4 h-4" />,
      action: () => {},
      shortcut: "⌘E",
    });
  }

  // Settings Actions - only for exact settings routes
  if (pathSegments.includes("settings")) {
    actions.push({
      id: "settings-quick",
      label: "Quick Settings",
      icon: <Settings className="w-4 h-4" />,
      action: () => {},
      shortcut: "⌘K",
    });
  }

  return actions;
};

// Global Quick Actions Hook
export const useQuickActions = () => {
  const { addQuickAction, removeQuickAction, updateQuickAction } = useHeader();

  const addGlobalQuickAction = (action: QuickAction) => {
    addQuickAction(action);
  };

  const removeGlobalQuickAction = (id: string) => {
    removeQuickAction(id);
  };

  const updateGlobalQuickAction = (id: string, updates: Partial<QuickAction>) => {
    updateQuickAction(id, updates);
  };

  return {
    addGlobalQuickAction,
    removeGlobalQuickAction,
    updateGlobalQuickAction,
  };
};
